import React, { useState, ReactNode } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export interface MCollapsibleCardProps {
  /** The title displayed in the card header */
  title: string | ReactNode;
  /** Content to display inside the collapsible area */
  children: ReactNode;
  /** Content that displays below the header but is always visible (not collapsible) */
  persistentContent?: ReactNode;
  /** Whether the card starts expanded. Default is false (collapsed) */
  defaultExpanded?: boolean;
  /** Controlled expanded state - if provided, component becomes controlled */
  expanded?: boolean;
  /** Callback when expanded state changes */
  onExpandedChange?: (expanded: boolean) => void;
  /** Additional className for the Card */
  className?: string;
  /** Additional className for the wrapper div */
  wrapperClassName?: string;
  /** Whether to use styled header (dark background with light text). Default is true */
  styledHeader?: boolean;
  /** Additional actions to render in the header (to the left of the expand icon) */
  headerActions?: ReactNode;
}

/**
 * A reusable collapsible card component with an expandable/collapsible content area.
 * 
 * Usage:
 * ```tsx
 * <MCollapsibleCard title="My Card Title">
 *   <p>Card content goes here</p>
 * </MCollapsibleCard>
 * 
 * // With controlled state
 * <MCollapsibleCard 
 *   title="Controlled Card" 
 *   expanded={isExpanded} 
 *   onExpandedChange={setIsExpanded}
 * >
 *   <p>Content</p>
 * </MCollapsibleCard>
 * ```
 */
const MCollapsibleCard = ({
  title,
  children,
  persistentContent,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onExpandedChange,
  className = "md-cell md-cell--12",
  wrapperClassName = "case-header",
  styledHeader = true,
  headerActions,
}: MCollapsibleCardProps) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  
  // Support both controlled and uncontrolled modes
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    const newExpanded = !expanded;
    if (!isControlled) {
      setInternalExpanded(newExpanded);
    }
    onExpandedChange?.(newExpanded);
  };

  const headerStyles = styledHeader
    ? {
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: "var(--card-header-bg-color)",
      }
    : {
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      };

  const textColor = styledHeader ? { color: "var(--card-header-font-color)" } : {};

  return (
    <div className={wrapperClassName}>
      <Card className={className}>
        <CardContent style={headerStyles} onClick={handleToggle}>
          {typeof title === "string" ? (
            <Typography variant="h6" sx={textColor}>
              {title}
            </Typography>
          ) : (
            title
          )}
          <div style={{ display: "flex", alignItems: "center" }}>
            {headerActions}
            <IconButton size="small" sx={textColor}>
              <ExpandMoreIcon className={`expand-icon ${expanded ? "expanded" : ""}`} />
            </IconButton>
          </div>
        </CardContent>
        {persistentContent && <CardContent>{persistentContent}</CardContent>}
        <Collapse in={expanded}>
          <CardContent>{children}</CardContent>
        </Collapse>
      </Card>
    </div>
  );
};

export default MCollapsibleCard;
